import { supabase } from '@/integrations/supabase/client';
import type { ChatMessage, ChatMetadata, SendMessageRequest, SendMessageResponse } from '@/types/leadChat';

/**
 * Send a chat message to the AI assistant
 */
export async function sendChatMessage(
  request: SendMessageRequest
): Promise<SendMessageResponse> {
  try {
    const { data, error } = await supabase.functions.invoke('lead-chat', {
      body: request
    });

    if (error) {
      console.error('Error calling lead-chat function:', error);
      return {
        success: false,
        error: error.message || 'Failed to send message'
      };
    }

    return data as SendMessageResponse;
  } catch (error) {
    console.error('Error in sendChatMessage:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    };
  }
}

/**
 * Get chat history for a lead
 */
export async function getChatHistory(leadId: string): Promise<ChatMessage[]> {
  try {
    const { data, error } = await supabase
      .from('lead_chat_messages')
      .select('*')
      .eq('lead_id', leadId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching chat history:', error);
      return [];
    }

    return (data || []).map(msg => ({
      id: msg.id,
      leadId: msg.lead_id,
      userId: msg.user_id,
      companyId: msg.company_id,
      role: msg.role as 'user' | 'assistant',
      content: msg.content,
      metadata: (msg.metadata as ChatMetadata) || {},
      createdAt: msg.created_at,
      updatedAt: msg.updated_at
    }));
  } catch (error) {
    console.error('Error in getChatHistory:', error);
    return [];
  }
}

/**
 * Clear chat history for a lead (optional feature)
 */
export async function clearChatHistory(leadId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('lead_chat_messages')
      .delete()
      .eq('lead_id', leadId);

    if (error) {
      console.error('Error clearing chat history:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in clearChatHistory:', error);
    return false;
  }
}
